import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { fetchAnnotations, saveAnnotations, clearAnnotationsError } from '../../stores/actions/annotations';
import CardAnnotations from '../../components/Card/CardAnnotations';

/**
 * Componente contextual de CardAnnotations, acoplado ao contexto
 * e regras de negocio do sistema
 */
const propTypes = {
  customerId: PropTypes.number,
  surveyId: PropTypes.number,
  /**
     * Estrategia de anotacaco ('simpleCross', 'multipleCross', 'simple', 'correlation', 'evolutive')
     */
  strategy: PropTypes.string.isRequired,
  /**
     * Chave que identifica o resultado a qual a anotacao esta atrelada, pode ser o id da configuracao,
     * um combo com o id da pesquisa e da pergunta principal, etc.
     */
  resultKey: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  initialText: PropTypes.shape({
    ops: PropTypes.arrayOf(
      PropTypes.shape({
        insert: PropTypes.string,
        attributes: PropTypes.object,
      }),
    ),
  }),
  annotationsTools: PropTypes.arrayOf(PropTypes.string),
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onFailure: PropTypes.func,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  placeholder: PropTypes.string,
};
const defaultProps = {
  annotationsTools: ['bold', 'italic', 'underline', 'strike', 'listOrdered', 'listBullet'],
  isLoading: false,
  title: 'Anotações',
  placeholder: 'Faça suas anotações',
};
function ContextualCardAnnotations(props) {
  // state from props
  const { annotationsTools, title, placeholder, onSave, onCancel, strategy, resultKey, onFailure, customerId, surveyId, initialText } = props;
  // state
  const [isLoading, setIsLoading] = useState(false);
  const [lastAnnotation, setLastAnnotation] = useState();
  const [subjected, setSubjected] = useState(false);
  // store
  const dispatch = useDispatch();
  const annotationState = useSelector((state) => state.annotations[strategy][resultKey]);

  /**
  * Dispara uma action para carergar as anotacoes do
  * resultado em questao
  */
  const fetchResultKeyAnnotations = useCallback(() => {
    if (!resultKey) return;
    fetchAnnotations(strategy, customerId, surveyId, resultKey, dispatch);
  }, [strategy, customerId, surveyId, resultKey, dispatch]);


  /**
     * Callback executado quando ha alteracao no resultado
     */
  useEffect(() => {
    fetchResultKeyAnnotations();
  }, [customerId, surveyId, resultKey, fetchResultKeyAnnotations]);

  /**
     * Verifica se as anotacoes estao sendo carregadas
     */
  const checkLoading = useCallback(() => {
    setIsLoading(annotationState && annotationState.running);
  }, [annotationState]);
  /**
     * Funcao que verifica se uma acao de anotacoes falhou,
     * seja ela de persistencia/update ou mesmo de recuperacao
     */
  const checkFailure = useCallback(() => {
    const annotationsError = annotationState && annotationState.error;
    if (annotationsError) {
      const errorType = annotationsError.fetch ? 'fetch' : 'save';
      if (annotationsError[errorType].status === 404) {
        return;
      }

      setSubjected(false);
      clearAnnotationsError(strategy, customerId, surveyId, resultKey, dispatch);
      onFailure && onFailure(errorType);
    }
  }, [annotationState, customerId, dispatch, onFailure, resultKey, strategy, surveyId]);

  /**
     * Carrega a ultima anotacao disponivel na store
     */
  const loadLastAnnotation = useCallback(() => {
    if (!annotationState || !annotationState.value) return;
    const newAnnotation = JSON.parse(annotationState.value.content);
    setLastAnnotation(newAnnotation);
  }, [annotationState]);

  /**
   * Funcao a ser executada quando a persistencia/atualizacao
   * de uma anotacao eh bem sucedida
   */
  const checkSaveSuccessful = useCallback(() => {
    subjected && !annotationState.error && !annotationState.running && onSave && onSave();
  }, [annotationState, subjected, onSave]);

  /**
   * Callback executado quando ha alteracao no estado
   * das anotacoes a qual o componente se refere, na store
   */
  useEffect(() => {
    checkLoading();
    loadLastAnnotation();
    checkSaveSuccessful();
    checkFailure();
  }, [annotationState, checkLoading, loadLastAnnotation, checkSaveSuccessful, checkFailure]);


  /**
     * Recupera a primeira anotacao a ser representada em tela,
     * caso tenha uma anotacao preterita (anteriormente salva),
     * ela eh retornada, caso contrario, retorna-se o initialText
     */
  function getFirstAnnotation() {
    return lastAnnotation || initialText;
  }

  /**
     * Dispara uma action solicitando a persistencia/atualizacao
     * de uma anotacao
     */
  function handleOnSave(annotation) {
    setSubjected(true);
    setIsLoading(true);
    saveAnnotations(strategy, customerId, surveyId, resultKey, annotation, dispatch);
  }

  function handleOnCancel() {
    onCancel && onCancel();
  }

  return (
    <CardAnnotations
      onSave={handleOnSave}
      onCancel={handleOnCancel}
      annotationsTools={annotationsTools}
      initialText={getFirstAnnotation()}
      isLoading={isLoading}
      title={title}
      placeholder={placeholder}
    />
  );
}


ContextualCardAnnotations.propTypes = propTypes;
ContextualCardAnnotations.defaultProps = defaultProps;

export default ContextualCardAnnotations;
